import React, { useEffect, useMemo, useState } from 'react';
import { DataGrid } from '@material-ui/data-grid';

export type GlobalLeaderboardCompany = {
  company?: string;
  ticker?: string;
  country?: string;
  stock_exchange_symbol?: string;
  adr?: boolean;
  industry?: string;
  value_score?: number;
  currency?: string;
  quote?: {
    price?: number;
    change?: string;
    change_percent?: string;
    change_percent_value?: number | null;
    change_percent_color?: string;
  };
  default_valuation?: {
    price_in_words?: string;
    recommendation?: string;
    recommendation_color?: string;
    decision?: string;
    margin_of_safety?: number;
    margin_of_safety_in_words?: string;
    payback_time?: { current_payback_time_number?: number };
  };
};

type Props = {
  data: GlobalLeaderboardCompany[];
  loading: boolean;
  sortModel: any[];
  onSortModelChange: (model: any[]) => void;
};

const Header = ({ label, help }: { label: string; help?: string }) => (
  <strong className="glb-column-title" title={help || label}>{label}</strong>
);

const formattedAmount = (value?: number) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';

  return value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const MoneyValue = ({ amount, currency, className = '' }: { amount?: number; currency?: string; className?: string }) => (
  <span className={`glb-money-value ${className}`.trim()}>
    <span>{formattedAmount(amount)}</span>
    {typeof amount === 'number' && Number.isFinite(amount) && currency && <small>{currency}</small>}
  </span>
);

function GlobalLeaderboardDataGrid({ data, loading, sortModel, onSortModelChange }: Props) {
  const [clientReady, setClientReady] = useState(false);

  useEffect(() => setClientReady(true), []);

  const rows = useMemo(() => data.map((item, index) => ({
    id: `${item.ticker || 'company'}-${index}`,
    ticker: item.ticker || '—',
    company: item.company || '—',
    listing: item.stock_exchange_symbol || '—',
    listingCountry: item.country || '—',
    isAdr: item.adr === true,
    industry: item.industry || '—',
    valueScore: item.value_score,
    consideration: item.default_valuation?.recommendation || "Don't Buy",
    decision: item.default_valuation?.decision || 'Undetermined',
    price: item.quote?.price,
    change: item.quote?.change_percent_value,
    changeLabel: item.quote?.change_percent || '—',
    marginOfSafety: item.default_valuation?.margin_of_safety,
    currency: item.currency,
    paybackTime: item.default_valuation?.payback_time?.current_payback_time_number,
    recommendationColor: item.default_valuation?.recommendation_color,
    changeColor: item.quote?.change_percent_color,
  })), [data]);

  const columns: any[] = useMemo(() => [
    {
      field: 'ticker',
      headerName: 'Ticker',
      width: 100,
      sortable: false,
      renderHeader: () => <Header label="Ticker" />,
      renderCell: (params: any) => (
        <a className="glb-ticker-link" href={`/ticker/${encodeURIComponent(params.value)}`} target="_blank" rel="noopener noreferrer">
          {params.value}
        </a>
      ),
    },
    { field: 'company', headerName: 'Company', width: 210, sortable: false, renderHeader: () => <Header label="Company" /> },
    {
      field: 'listing',
      headerName: 'Listing',
      width: 175,
      renderHeader: () => <Header label="Listing" help="Primary exchange, company country, and ADR status" />,
      renderCell: (params: any) => (
        <span className="glb-listing-cell" title={`${params.row.listingCountry}${params.row.isAdr ? ' · ADR' : ''}`}>
          <strong>{params.value}</strong>
          <small>{params.row.listingCountry}{params.row.isAdr && <span> · ADR</span>}</small>
        </span>
      ),
    },
    { field: 'industry', headerName: 'Industry', width: 215, renderHeader: () => <Header label="Industry" /> },
    { field: 'valueScore', headerName: 'Value Score', width: 125, renderHeader: () => <Header label="Value Score" help="Decode Investing score; this leaderboard includes scores of 90 or higher" /> },
    {
      field: 'consideration',
      headerName: 'Consideration',
      width: 145,
      renderHeader: () => <Header label="Consideration" help="A concise interpretation of the valuation decision" />,
      renderCell: (params: any) => (
        <span className={`glb-consideration glb-consideration-${params.row.recommendationColor || 'neutral'}`}>
          {params.value}
        </span>
      ),
    },
    { field: 'decision', headerName: 'Decision', width: 175, renderHeader: () => <Header label="Decision" help="Valuation result using the default assumptions" /> },
    {
      field: 'price',
      headerName: 'Price',
      width: 125,
      renderHeader: () => <Header label="Price" help="Latest available quote, shown with its ISO currency code when available" />,
      renderCell: (params: any) => (
        <MoneyValue
          amount={params.value}
          currency={params.row.currency}
          className={`glb-change-${params.row.changeColor || 'neutral'}`}
        />
      ),
    },
    {
      field: 'change',
      headerName: 'Change',
      type: 'number',
      width: 105,
      renderHeader: () => <Header label="Change" help="Latest daily percentage change; a dash means no live quote was available" />,
      renderCell: (params: any) => <span className={`glb-change-${params.row.changeColor || 'neutral'}`}>{params.row.changeLabel}</span>,
    },
    {
      field: 'marginOfSafety',
      headerName: 'Margin of Safety',
      sortable: false,
      width: 175,
      renderHeader: () => <Header label="Margin of Safety" help="Estimated value cushion using the default valuation" />,
      renderCell: (params: any) => <MoneyValue amount={params.value} currency={params.row.currency} />,
    },
    {
      field: 'paybackTime',
      headerName: 'Payback Time',
      width: 145,
      renderHeader: () => <Header label="Payback Time" help="Estimated years for business earnings to repay the purchase price" />,
      renderCell: (params: any) => params.value ? `${params.value} years` : 'Unpredictable',
    },
    { field: 'recommendationColor', hide: true },
    { field: 'changeColor', hide: true },
  ], []);

  // The repository's alpha Material UI DataGrid build is browser-only when
  // controlled sorting is enabled, so mount the table after hydration.
  if (!clientReady) {
    return <div className="glb-grid-placeholder" role="status">Loading interactive company table…</div>;
  }

  return (
    <div className="global-leaderboard-grid" aria-label="Global company leaderboard">
      <style>{`
        .global-leaderboard-grid {
          width: 100%;
          min-width: 0;
        }
        .global-leaderboard-grid .MuiDataGrid-root {
          border: 0;
          color: #172033;
          font-family: inherit;
        }
        .global-leaderboard-grid .MuiDataGrid-columnHeaders {
          background: #f7f9fc;
          border-bottom: 1px solid #dfe5ef;
        }
        .global-leaderboard-grid .MuiDataGrid-columnHeader,
        .global-leaderboard-grid .MuiDataGrid-cell {
          border-bottom: 1px solid #edf0f5;
        }
        .global-leaderboard-grid .MuiDataGrid-cell:focus,
        .global-leaderboard-grid .MuiDataGrid-columnHeader:focus,
        .global-leaderboard-grid .MuiDataGrid-cell:focus-within,
        .global-leaderboard-grid .MuiDataGrid-columnHeader:focus-within {
          outline: 2px solid transparent;
        }
        .global-leaderboard-grid .MuiDataGrid-row.glb-row-green { background: #f0fdf4; }
        .global-leaderboard-grid .MuiDataGrid-row.glb-row-red { background: #fff6f5; }
        .global-leaderboard-grid .MuiDataGrid-row.glb-row-neutral { background: #f8fafc; }
        .global-leaderboard-grid .MuiDataGrid-row.glb-row-green:hover { background: #dcfae6; }
        .global-leaderboard-grid .MuiDataGrid-row.glb-row-red:hover { background: #feeceb; }
        .global-leaderboard-grid .MuiDataGrid-row.glb-row-neutral:hover { background: #eef2f6; }
        .global-leaderboard-grid .MuiDataGrid-footerContainer {
          background: #fff;
          border-top: 1px solid #dfe5ef;
        }
        .glb-column-title {
          color: #344054;
          font-size: .78rem;
          letter-spacing: .015em;
        }
        .glb-ticker-link {
          color: #175cd3;
          font-weight: 800;
          text-decoration: none;
        }
        .glb-ticker-link:hover { text-decoration: underline; }
        .glb-listing-cell {
          display: flex;
          min-width: 0;
          flex-direction: column;
          justify-content: center;
          line-height: 1.15;
        }
        .glb-listing-cell strong {
          overflow: hidden;
          color: #344054;
          font-size: .78rem;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .glb-listing-cell small {
          overflow: hidden;
          margin-top: 2px;
          color: #667085;
          font-size: .67rem;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .glb-listing-cell small span { color: #175cd3; font-weight: 750; }
        .glb-money-value { display: inline-flex; align-items: baseline; gap: .28rem; white-space: nowrap; }
        .glb-money-value small { color: #98a2b3; font-size: .65rem; font-weight: 650; letter-spacing: .025em; }
        .glb-consideration {
          display: inline-flex;
          align-items: center;
          border-radius: 999px;
          padding: .2rem .58rem;
          font-size: .75rem;
          font-weight: 750;
          line-height: 1.2;
          background: #eef2f6;
          color: #475467;
        }
        .glb-consideration-green { background: #dcfae6; color: #067647; }
        .glb-consideration-red { background: #fee4e2; color: #b42318; }
        .glb-change-green { color: #067647; font-weight: 650; }
        .glb-change-red { color: #b42318; font-weight: 650; }
        .glb-change-neutral { color: #667085; }
        .dark-mode .global-leaderboard-grid .MuiDataGrid-root { color: #e4e7ec; }
        .dark-mode .global-leaderboard-grid .MuiDataGrid-columnHeaders { background: #161b26; border-color: #303744; }
        .dark-mode .global-leaderboard-grid .MuiDataGrid-columnHeader,
        .dark-mode .global-leaderboard-grid .MuiDataGrid-cell { border-color: #252c38; }
        .dark-mode .global-leaderboard-grid .MuiDataGrid-row.glb-row-green { background: rgba(5, 122, 85, .11); }
        .dark-mode .global-leaderboard-grid .MuiDataGrid-row.glb-row-red { background: rgba(180, 35, 24, .11); }
        .dark-mode .global-leaderboard-grid .MuiDataGrid-row.glb-row-neutral { background: rgba(71, 84, 103, .12); }
        .dark-mode .global-leaderboard-grid .MuiDataGrid-row.glb-row-green:hover { background: rgba(5, 150, 105, .19); }
        .dark-mode .global-leaderboard-grid .MuiDataGrid-row.glb-row-red:hover { background: rgba(217, 45, 32, .18); }
        .dark-mode .global-leaderboard-grid .MuiDataGrid-row.glb-row-neutral:hover { background: rgba(71, 84, 103, .22); }
        .dark-mode .global-leaderboard-grid .MuiDataGrid-footerContainer { background: #111722; border-color: #303744; }
        .dark-mode .global-leaderboard-grid .MuiTablePagination-root,
        .dark-mode .global-leaderboard-grid .MuiIconButton-root { color: #d0d5dd; }
        .dark-mode .glb-column-title { color: #d0d5dd; }
        .dark-mode .glb-ticker-link { color: #84adff; }
        .dark-mode .glb-listing-cell strong { color: #d0d5dd; }
        .dark-mode .glb-listing-cell small { color: #858e9d; }
        .dark-mode .glb-listing-cell small span { color: #84adff; }
        .dark-mode .glb-money-value small { color: #858e9d; }
        .dark-mode .glb-consideration { background: #29303d; color: #d0d5dd; }
        .dark-mode .glb-consideration-green { background: rgba(5, 150, 105, .2); color: #6ce9a6; }
        .dark-mode .glb-consideration-red { background: rgba(217, 45, 32, .2); color: #fda29b; }
        .dark-mode .glb-change-green { color: #6ce9a6; }
        .dark-mode .glb-change-red { color: #fda29b; }
      `}</style>
      <DataGrid
        autoHeight
        rows={rows}
        columns={columns}
        pageSize={25}
        rowsPerPageOptions={[25, 50, 100]}
        density="compact"
        rowHeight={48}
        loading={loading}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        disableSelectionOnClick
        getRowClassName={(params: any) => {
          if (params.row.recommendationColor === 'green') return 'glb-row-green';
          if (params.row.recommendationColor === 'red' || !params.row.recommendationColor) return 'glb-row-red';
          return 'glb-row-neutral';
        }}
      />
    </div>
  );
}

export default GlobalLeaderboardDataGrid;
